import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Globe, MapPin } from "lucide-react";

export default function Attractions() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/attractions").then((r) => setList(r.data)); }, []);

  const dining = useMemo(() => list.filter((a) => a.category === "Dining"), [list]);
  const others = useMemo(() => list.filter((a) => a.category !== "Dining"), [list]);

  return (
    <div data-testid="attractions-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 anim-fadeup">
      <div className="section-label">Beyond the Field</div>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-slate-950 mt-3">Local attractions.</h1>
      <p className="mt-3 text-slate-600 max-w-2xl">
        Once you're chocked and tied down, here's what to do near Lake Cumberland Regional Airport — from the lake itself to
        downtown Somerset and the gorges of Big South Fork.
      </p>

      <Section title="Things to See & Do" items={others} testid="attractions-other" />

      <div className="my-16 flex items-center gap-6" aria-hidden="true">
        <div className="flex-1 h-px bg-slate-200" />
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-slate-400">Restaurants & Cafés</div>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      <Section title="Where to Eat" items={dining} testid="attractions-dining" hideHeader />
    </div>
  );
}

function Section({ title, items, testid, hideHeader }) {
  if (!items || items.length === 0) return null;
  return (
    <section data-testid={testid}>
      {!hideHeader && <div className="text-xs font-mono uppercase tracking-widest text-sky-600 mb-6 mt-12">{title}</div>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((a) => (
          <article
            key={a.id}
            data-testid={`attraction-${a.id}`}
            className="bg-white border border-slate-200 overflow-hidden flex flex-col"
          >
            {a.image_url && (
              <div className="h-48 overflow-hidden">
                <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-6 flex flex-col flex-1">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs font-mono uppercase tracking-widest text-sky-600">{a.category}</div>
                {a.distance && (
                  <div className="text-xs font-mono uppercase tracking-widest text-slate-400">{a.distance}</div>
                )}
              </div>
              <div className="font-display text-xl font-semibold text-slate-950 mt-2 leading-tight">{a.name}</div>
              {a.description && (
                <p className="text-sm text-slate-600 mt-2 leading-relaxed flex-1">{a.description}</p>
              )}
              <dl className="mt-4 space-y-1 text-sm">
                {a.address && (
                  <div className="flex items-start gap-2 text-slate-700">
                    <MapPin className="h-4 w-4 mt-0.5 text-slate-400 shrink-0" /> {a.address}
                  </div>
                )}
                {a.website && (
                  <a target="_blank" rel="noreferrer" href={a.website}
                     className="flex items-center gap-2 text-slate-700 hover:text-sky-600">
                    <Globe className="h-4 w-4" /> Visit website
                  </a>
                )}
              </dl>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
